import { AInput } from "@/admin/form/Fields.tsx";
import { locationCreateMutation, locationUpdateMutation } from "@/admin/locations/locationMutations.ts";
import { FormField } from "@/components/forms/FormField.tsx";
import { WCheckbox } from "@/components/forms/WCheckbox.tsx";
import { WModalForm } from "@/components/forms/WModalForm.tsx";
import { WNumberField } from "@/components/forms/WNumberField.tsx";
import { WTextField } from "@/components/forms/WTextField.tsx";
import { controllerProps, formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { useConfig } from "@/config.ts";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { CountrySelect } from "@/modules/location/CountrySelect.tsx";
import { getIso2CountryInfo } from "@/modules/location/locationCountry.ts";
import type { Location } from "@warrenio/api-spec/spec.oats.gen";
import { notNull } from "@warrenio/utils/notNull";
import type { ReactNode } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";

export function AddLocationButton({ button }: { button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditLocationModalContent />
        </WModal>
    );
}

export function EditLocationButton({ item, button }: { item: Location; button: ReactNode }) {
    return (
        <WModal button={button}>
            <AddEditLocationModalContent item={item} />
        </WModal>
    );
}

interface AddEditLocationInputs {
    order_nr: number | undefined;
    slug: string;
    display_name: string;
    description: string;
    create_resource_disabled: boolean;
    is_preferred: boolean;
    is_published: boolean;
    country_code: string;
}

function AddEditLocationModalContent({ item }: { item?: Location }) {
    const { siteCountry } = useConfig();
    const createMutation = useStandardMutation(locationCreateMutation);
    const updateMutation = useStandardMutation(locationUpdateMutation);

    const defaultCountryCode = item?.country_code ?? notNull(getIso2CountryInfo(siteCountry)?.code.iso3);
    const isEditMode = !!item;

    const onSubmit: SubmitHandler<AddEditLocationInputs> = async (inputs) => {
        const {
            order_nr,
            slug,
            display_name,
            description,
            create_resource_disabled,
            is_preferred,
            is_published,
            country_code,
        } = inputs;

        if (isEditMode) {
            await updateMutation.mutateAsync({
                body: {
                    ...item,
                    order_nr: order_nr ? Number(order_nr) : 0,
                    display_name,
                    description,
                    create_resource_disabled,
                    is_preferred,
                    is_published,
                    country_code,
                },
                location: slug,
            });
        } else {
            await createMutation.mutateAsync({
                body: {
                    order_nr: order_nr ? Number(order_nr) : 0,
                    display_name,
                    description,
                    create_resource_disabled,
                    is_preferred,
                    is_published,
                    country_code,
                },
                location: slug,
            });
        }
    };

    const form = useForm<AddEditLocationInputs>({
        mode: "onBlur",
        reValidateMode: "onBlur",

        defaultValues: {
            order_nr: item?.order_nr ?? undefined,
            slug: item?.slug ?? "",
            display_name: item?.display_name ?? "",
            description: item?.description ?? "",
            create_resource_disabled: !!item?.create_resource_disabled,
            is_preferred: item?.is_preferred ?? false,
            is_published: item?.is_published ?? false,
            country_code: defaultCountryCode.toUpperCase(),
        },
    });

    const { control, watch } = form;

    return (
        <WModalForm
            title={isEditMode ? "Edit Location" : "Add New Location"}
            label={isEditMode ? "Edit" : "Add"}
            form={form}
            onSubmit={onSubmit}
        >
            <Controller
                control={control}
                name="order_nr"
                render={(props) => (
                    <WNumberField
                        autoFocus
                        headless
                        wide
                        label="Order Nr"
                        {...formPropsToAriaProps(props)}
                        formatOptions={{
                            maximumFractionDigits: 0,
                        }}
                    >
                        <AInput />
                    </WNumberField>
                )}
            />

            <Controller
                control={control}
                name="slug"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired
                        isReadOnly={isEditMode}
                        wide
                        headless
                        label="Slug"
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="display_name"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField isRequired wide headless label="Name" value={field.value} onChange={field.onChange}>
                        <AInput />
                    </WTextField>
                )}
            />

            <Controller
                control={control}
                name="description"
                {...controllerProps({ isRequired: true })}
                render={({ field }) => (
                    <WTextField
                        isRequired
                        wide
                        headless
                        label="Description"
                        value={field.value}
                        onChange={field.onChange}
                    >
                        <AInput />
                    </WTextField>
                )}
            />

            <FormField label=" " wide headless>
                <Controller
                    control={control}
                    name="create_resource_disabled"
                    render={({ field }) => (
                        <WCheckbox isSelected={field.value} onChange={field.onChange}>
                            Create Resource Disabled
                        </WCheckbox>
                    )}
                />
            </FormField>

            <FormField label=" " wide headless>
                <Controller
                    control={control}
                    name="is_preferred"
                    render={({ field }) => (
                        <WCheckbox
                            isDisabled={watch("create_resource_disabled")}
                            isSelected={field.value}
                            onChange={field.onChange}
                        >
                            Is Preferred
                        </WCheckbox>
                    )}
                />
            </FormField>

            <FormField label=" " wide headless>
                <Controller
                    control={control}
                    name="is_published"
                    render={({ field }) => (
                        <WCheckbox isSelected={field.value} onChange={field.onChange}>
                            Is Published
                        </WCheckbox>
                    )}
                />
            </FormField>

            <FormField label="Country" isRequired wide headless>
                <Controller
                    control={control}
                    name="country_code"
                    {...controllerProps({ isRequired: true })}
                    render={({ field }) => (
                        <CountrySelect
                            valueKey={field.value}
                            onChange={(country) => field.onChange(country.code.iso3)}
                        />
                    )}
                />
            </FormField>
        </WModalForm>
    );
}
